import clsx from 'clsx';
import type { FunctionComponent, InputHTMLAttributes, Ref } from 'react';
import { Fragment, useCallback, useId, useRef } from 'react';

import { IconSearch } from '../../core/icons';
import { Input } from '../../core/input/Input';
import { useKeyboardShortcutsEnabled } from '../../core/keyboard-shortcuts/KeyboardShortcutsContext';
import { useHotkey } from '../../core/keyboard-shortcuts/use-hotkey';
import { useKeyboardShortcutDisplay } from './use-keyboard-shortcut-display';

export const KeyboardHint: FunctionComponent<{ readonly children: React.ReactNode; readonly className?: string }> = ({
    children,
    className,
}) => (
    <span
        className={clsx('text-text-content-subtle text-body-xxs font-normal select-none whitespace-nowrap', className)}
    >
        {children}
    </span>
);

type BaseSearchInputProps = Omit<InputHTMLAttributes<HTMLInputElement>, 'size' | 'type'> & {
    readonly ref?: Ref<HTMLInputElement>;
    readonly rightElement?: React.ReactNode;
    readonly size?: 'default' | 'sm';
    /** `pill` for search fields in title bars, next to small pill buttons. */
    readonly shape?: 'default' | 'pill';
};

type SearchInputWithHotkeyProps = BaseSearchInputProps & {
    readonly hotkey: string;
    readonly hotkeyScope: string;
};

type SearchInputWithoutHotkeyProps = BaseSearchInputProps & {
    readonly hotkey?: undefined;
    readonly hotkeyScope?: undefined;
};

export type SearchInputProps = SearchInputWithHotkeyProps | SearchInputWithoutHotkeyProps;

const SearchInputBase: FunctionComponent<BaseSearchInputProps & { readonly inputRef?: Ref<HTMLInputElement> }> = ({
    className,
    id,
    inputRef,
    rightElement,
    shape = 'default',
    size = 'default',
    ...props
}) => {
    const generatedId = useId();
    const inputId = id ?? generatedId;
    const isSmall = size === 'sm';

    return (
        <Input
            ref={inputRef}
            className={clsx(
                isSmall && 'gap-s!',
                isSmall && shape !== 'pill' && 'py-s!',
                !isSmall && shape === 'pill' && 'h-control!',
                className,
            )}
            endElement={rightElement}
            id={inputId}
            shape={shape}
            inputClassName={clsx(
                'text-text-muted placeholder:text-text-content-faint',
                isSmall ? 'text-xxs leading-[1rem]' : 'text-sm leading-[1.25rem]',
            )}
            startElement={
                <label className="flex items-center justify-center shrink-0 cursor-text opacity-50" htmlFor={inputId}>
                    <IconSearch size={16} />
                </label>
            }
            type="text"
            variant="search"
            {...props}
        />
    );
};

const SearchInputWithHotkey: FunctionComponent<SearchInputWithHotkeyProps & { readonly enabled: boolean }> = ({
    enabled,
    hotkey,
    hotkeyScope,
    rightElement,
    ...props
}) => {
    const keyboardShortcut = useKeyboardShortcutDisplay(hotkey);
    const searchInputRef = useRef<HTMLInputElement>(null);

    const focusSearchInput = useCallback((): void => {
        searchInputRef.current?.focus();
    }, []);

    useHotkey({ enabled, hotkey, onTrigger: focusSearchInput, scope: hotkeyScope });

    const combinedRightElement = enabled ? (
        <Fragment>
            {rightElement}
            <KeyboardHint>{keyboardShortcut}</KeyboardHint>
        </Fragment>
    ) : (
        rightElement
    );

    return <SearchInputBase {...props} inputRef={searchInputRef} rightElement={combinedRightElement} />;
};

export const SearchInput: FunctionComponent<SearchInputProps> = (props) => {
    const keyboardShortcutsEnabled = useKeyboardShortcutsEnabled();

    if (props.hotkey !== undefined && props.hotkeyScope !== undefined) {
        return <SearchInputWithHotkey {...props} enabled={keyboardShortcutsEnabled} />;
    }

    return <SearchInputBase {...props} />;
};

SearchInput.displayName = 'SearchInput';
